import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations } from "@/lib/translations";

const LanguageContext = createContext(null);

const getInitialLang = () => {
  if (typeof window === "undefined") return "en";
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("lang");
  if (fromUrl === "ar" || fromUrl === "en") return fromUrl;
  try {
    const saved = window.localStorage.getItem("dx_lang");
    if (saved === "ar" || saved === "en") return saved;
  } catch (e) { console.warn("localStorage read failed:", e); }
  return "en";
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(getInitialLang);

  useEffect(() => {
    const root = document.documentElement;
    const dir = translations[lang].dir;
    root.setAttribute("lang", lang);
    root.setAttribute("dir", dir);
    try { window.localStorage.setItem("dx_lang", lang); }
    catch (e) { console.warn("localStorage write failed:", e); }

    // Keep the URL in sync so hreflang alternates resolve correctly and
    // shared links land in the right language.
    const params = new URLSearchParams(window.location.search);
    const current = params.get("lang");
    if (lang === "ar" && current !== "ar") {
      params.set("lang", "ar");
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}${window.location.hash}`);
    } else if (lang === "en" && current) {
      params.delete("lang");
      const qs = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? "?" + qs : ""}${window.location.hash}`);
    }
  }, [lang]);

  const toggleLang = useCallback(() => {
    setLang((prev) => (prev === "en" ? "ar" : "en"));
  }, []);

  const t = translations[lang];

  const value = useMemo(
    () => ({ lang, setLang, toggleLang, t, isRTL: lang === "ar" }),
    [lang, toggleLang, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
